/**
 * Routing helper for the [refinando fp_<id>] prefix that the Refinar button
 * injects into the chat input. Pure function — no React, no fetch, no globals.
 */

/**
 * Minimal shape expected from each turn. Keeping this local (rather than
 * importing the full ChatTurn from chat.tsx) avoids a circular import between
 * the route module and this library file, and keeps the helper testable without
 * pulling in the entire chat component tree.
 */
interface TurnForRefineRouting {
  id: string;
  factProposal?: { draft_id: string; status: string; assistant_message_id?: string };
  factProposals?: { draft_id: string; status: string; assistant_message_id?: string }[];
}

export type RefineModeRoute =
  | { kind: "refine"; turnId: string; messageId: string; draftId: string; instructions: string }
  | { kind: "passthrough" };

const REFINE_MODE_PREFIX = /^\[refinando (fp_[A-Za-z0-9_-]+)\]\s*([\s\S]+)$/;
const REFINE_KNOWLEDGE_PREFIX = /^\[refinando knowledge_([0-9a-fA-F-]{36})\]\s*([\s\S]+)$/;

export type RefineKnowledgeRoute =
  | { kind: "refine-knowledge"; itemId: string; instructions: string }
  | { kind: "passthrough" };

/**
 * Sibling of `parseRefineModeInput` for the knowledge-refine flow. The
 * prefix carries an item UUID (set by `routes/chat.tsx` when the page
 * mounts with `?refine-knowledge=<id>`). Any deviation (missing
 * prefix, mismatched id, empty instructions) falls through to
 * passthrough — the caller routes those through the regular chat
 * completion path or simply ignores them while refine-knowledge mode
 * is active.
 */
export function parseRefineKnowledgeInput(
  message: string,
  expectedItemId: string | null,
): RefineKnowledgeRoute {
  if (expectedItemId === null) return { kind: "passthrough" };
  const match = REFINE_KNOWLEDGE_PREFIX.exec(message);
  if (match === null) return { kind: "passthrough" };
  const itemId = match[1];
  const rawInstructions = match[2];
  if (itemId === undefined || rawInstructions === undefined) {
    return { kind: "passthrough" };
  }
  if (itemId.toLowerCase() !== expectedItemId.toLowerCase()) {
    // Active refine-knowledge mode is bound to a specific id; refuse
    // to route an off-target send. The chat surface keeps the input
    // for the operator to correct.
    return { kind: "passthrough" };
  }
  const instructions = rawInstructions.trim();
  if (instructions === "") return { kind: "passthrough" };
  return { kind: "refine-knowledge", itemId, instructions };
}

/**
 * Decide whether a chat input message should be routed to the refine
 * endpoint instead of /chat. The routing is opt-in per turn — the user
 * must explicitly carry the `[refinando fp_<id>] ` prefix (set by
 * clicking the Refinar button on a pending card). Any deviation
 * (missing prefix, unknown draft_id, empty instructions) falls through
 * to a normal chat turn.
 */
export function parseRefineModeInput(
  message: string,
  turns: ReadonlyArray<TurnForRefineRouting>,
): RefineModeRoute {
  const match = REFINE_MODE_PREFIX.exec(message);
  if (match === null) return { kind: "passthrough" };
  // Capture groups 1 and 2 are always present when the regex matches
  // (both are required, non-optional groups), but TypeScript widens
  // indexed regex match results to `string | undefined`. Narrow them
  // explicitly rather than using a non-null assertion.
  const draftId = match[1];
  const rawInstructions = match[2];
  if (draftId === undefined || rawInstructions === undefined) {
    return { kind: "passthrough" };
  }
  const instructions = rawInstructions.trim();
  if (instructions === "") return { kind: "passthrough" };
  const targetTurn = turns.find((t) => proposalForDraft(t, draftId) !== undefined);
  const targetProposal =
    targetTurn === undefined ? undefined : proposalForDraft(targetTurn, draftId);
  if (targetTurn === undefined || targetProposal === undefined) {
    return { kind: "passthrough" };
  }
  // Backend lifecycle endpoints key on the real `chat_messages.id`, which
  // the SSE `fact_proposal` event carries as `assistant_message_id`. The
  // local React turn id is a fresh `crypto.randomUUID()` and would 404
  // against /chat/messages/{id}/fact-proposal/refine on live-streamed
  // turns. On rehydration both ids collapse to msg.id, hence the
  // fallback — mirrors the same pattern in FactProposalCardWithContext.
  const messageId = targetProposal.assistant_message_id ?? targetTurn.id;
  return { kind: "refine", turnId: targetTurn.id, messageId, draftId, instructions };
}

function proposalForDraft(
  turn: TurnForRefineRouting,
  draftId: string,
): { draft_id: string; status: string; assistant_message_id?: string } | undefined {
  return [turn.factProposal, ...(turn.factProposals ?? [])].find(
    (proposal) =>
      proposal !== undefined && proposal.draft_id === draftId && proposal.status === "pending",
  );
}
